"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSelectedAudioDevices = void 0;
const react_1 = require("react");
const toolbox_1 = require("@streem/toolbox");
const find_matching_device_by_group_id_1 = require("../util/find_matching_device_by_group_id");
const isAudioKind = (kind) => {
    return ['audioinput', 'audiooutput'].includes(kind);
};
const useFindAndSelectMatchingDeviceCreator = (audioDevices, onSelectMicrophone, onSelectSpeaker, setSelectedMicrophone, setSelectedSpeaker) => {
    const findAndSelectMatchingDevice = (0, react_1.useCallback)((device) => {
        (0, toolbox_1.invariant)(isAudioKind(device.kind), 'Invalid device kind');
        const kind = device.kind;
        // default to audioinput as device kind (audiooutput as matching device)
        let matchingDeviceKind = 'audiooutput';
        let onSelectMatchingDeviceFn = onSelectSpeaker;
        let setSelectedMatchingDeviceFn = setSelectedSpeaker;
        // override if device kind is audiooutput
        if (kind === 'audiooutput') {
            matchingDeviceKind = 'audioinput';
            onSelectMatchingDeviceFn = onSelectMicrophone;
            setSelectedMatchingDeviceFn = setSelectedMicrophone;
        }
        const matchingDevice = (0, find_matching_device_by_group_id_1.findMatchingDeviceByGroupId)(audioDevices, matchingDeviceKind, device.groupId);
        if (matchingDevice) {
            setSelectedMatchingDeviceFn(matchingDevice);
            onSelectMatchingDeviceFn(matchingDevice);
            return true;
        }
        return false;
    }, [
        audioDevices,
        onSelectMicrophone,
        onSelectSpeaker,
        setSelectedMicrophone,
        setSelectedSpeaker,
    ]);
    return findAndSelectMatchingDevice;
};
// This hook is currently only used for in-call, but in an attempt to make it more generic, it accepts
//      onSelectMicrophone and onSelectSpeaker functions as arguments.  (For the in-call flow,
//      selecting a microphone or speaker uses the mediaController, but outside of a call, it will
//      use getUserMedia/setSinkId)
const useSelectedAudioDevices = (canSelectSpeaker, onSelectMicrophone, onSelectSpeaker, localDevices, localAudioLabel) => {
    const audioDevices = (0, react_1.useMemo)(() => {
        return ((localDevices === null || localDevices === void 0 ? void 0 : localDevices.filter(device => ['audiooutput', 'audioinput'].includes(device.kind))) ||
            []);
    }, [localDevices]);
    const [selectedMicrophone, setSelectedMicrophone] = (0, react_1.useState)(null);
    const [selectedSpeaker, setSelectedSpeaker] = (0, react_1.useState)(null);
    const findAndSelectMatchingDevice = useFindAndSelectMatchingDeviceCreator(audioDevices, onSelectMicrophone, onSelectSpeaker, setSelectedMicrophone, setSelectedSpeaker);
    // try to keep the user's microphone and speaker selections consistent.  If the user chooses a new microphone,
    //    try to find a speaker that matches the microphone's group (apply the same logic in reverse if the user chooses
    //    a new speaker).  This is likely what the user wants (for example, if they select their headset as speaker, they
    //    probably want to use the micropohone on their headset as well).
    (0, react_1.useEffect)(() => {
        // if we have a preferred microphone selected, set it as the selected microphone
        const preferredMicrophoneId = (0, toolbox_1.getPreferredAudioInputId)();
        const preferredSpeakerId = (0, toolbox_1.getPreferredAudioOutputId)();
        const preferredMicrophone = preferredMicrophoneId &&
            audioDevices.find(device => device.deviceId === preferredMicrophoneId && device.kind === 'audioinput');
        const preferredSpeaker = preferredSpeakerId &&
            audioDevices.find(device => device.deviceId === preferredSpeakerId && device.kind === 'audiooutput');
        if (preferredSpeaker) {
            setSelectedSpeaker(preferredSpeaker);
            if (canSelectSpeaker) {
                const foundMatchingDevice = findAndSelectMatchingDevice(preferredSpeaker);
                // if there is no matching microphone for the preferred speaker, look for a preferred microphone
                if (!foundMatchingDevice && preferredMicrophone) {
                    setSelectedMicrophone(preferredMicrophone);
                    // there is no need to call onSelectMicrophone here because the microphone was already selected
                    //    (either by MediaController when the call starts or by the callback when the user changes it in the UI)
                }
                else if (!foundMatchingDevice) {
                    // if there is no preferred microphone, use the current microphone (based on mediaController.localAudioLabel)
                    const currentMicrophone = audioDevices.find(device => device.label === localAudioLabel);
                    setSelectedMicrophone(currentMicrophone || null);
                    // there is no need to call onSelectMicrophone here because the microphone was already selected
                }
            }
        }
        else if (preferredMicrophone) {
            setSelectedMicrophone(preferredMicrophone);
            if (canSelectSpeaker) {
                // if we hit this branch of the if statement, we know that there is no preferred speaker.  Look for a group match for
                //     the preferred microphone and if there is one, set it as the selected speaker
                findAndSelectMatchingDevice(preferredMicrophone);
            }
        }
        else if (localAudioLabel && audioDevices.length > 0) {
            // if we hit this branch of the if statement, we know that there is no preferred microphone or speaker.  Use the currently
            //    selected microphone (based on mediaController.localAudioLabel)
            const currentMicrophone = audioDevices.find(device => device.label === localAudioLabel);
            setSelectedMicrophone(currentMicrophone || null);
            if (currentMicrophone && canSelectSpeaker) {
                // if there is a current microphone, try to find a matching speaker.  If there is no matching speaker, do not change the
                //     selected speaker.
                findAndSelectMatchingDevice(currentMicrophone);
            }
        }
    }, [
        audioDevices,
        localAudioLabel,
        canSelectSpeaker,
        findAndSelectMatchingDevice,
        setSelectedMicrophone,
        setSelectedSpeaker,
    ]);
    return {
        selectedMicrophone,
        setSelectedMicrophone,
        selectedSpeaker,
        setSelectedSpeaker,
    };
};
exports.useSelectedAudioDevices = useSelectedAudioDevices;
//# sourceMappingURL=use_selected_audio_devices.js.map